import React, { useEffect, useState } from "react";
import { ShieldCheck, Warning, XCircle, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// Sprint 91 — Portfolio Audit. Grades what was actually built against the rules
// that were supposed to build it, including the imported field (shadow) wall.
const TONE = {
  pass: { cls: "text-[#39FF14] border-[#39FF14]/50", Icon: CheckCircle, label: "PASS" },
  warn: { cls: "text-[#FFCC00] border-[#FFCC00]/50", Icon: Warning, label: "CHECK" },
  fail: { cls: "text-[#FF3B30] border-[#FF3B30]/60", Icon: XCircle, label: "FAIL" },
};

export default function PortfolioAudit() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => {
    setBusy(true);
    setErr("");
    return api.portfolioAudit()
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Audit unavailable"))
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    load();
    const on = () => load();
    window.addEventListener("wp:lineups-changed", on);
    window.addEventListener("wp:auto-build-done", on);
    return () => {
      window.removeEventListener("wp:lineups-changed", on);
      window.removeEventListener("wp:auto-build-done", on);
    };
  }, []);
  const top = TONE[data?.status] || TONE.warn;
  return (
    <section className="bg-[#121212] border border-zinc-800 p-4" data-testid="portfolio-audit">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="heading text-lg font-black uppercase inline-flex items-center gap-2">
            <ShieldCheck size={16} weight="fill" className="text-[#39FF14]" /> Portfolio Audit
          </div>
          <div className="text-[11px] text-zinc-400 mt-1 max-w-2xl leading-relaxed">
            Every rule that was supposed to shape this portfolio, graded against the lineups that came
            out — count, duplicates, overlap, exposure and crown caps, salary, ownership, and how far
            you sit off the field's own imported lineups.
          </div>
        </div>
        <div className="text-right">
          {data?.ready ? (
            <span data-testid="audit-status"
              className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${top.cls}`}>
              {top.label} · {data.total_lineups}/{data.total_requested} lineups
            </span>
          ) : null}
          <div className="mono text-[9px] text-zinc-500 mt-1" data-testid="audit-shadow-note">
            {data?.shadow_lineups
              ? `${data.shadow_lineups} imported field lineups · ${(data.shadow_sources || []).join(" + ")}`
              : "no imported field lineups on this slate"}
          </div>
          <button type="button" onClick={load} disabled={busy} data-testid="audit-refresh"
            className="mt-1 mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#39FF14] inline-flex items-center gap-1">
            <ArrowsClockwise size={11} className={busy ? "animate-spin" : ""} /> {busy ? "auditing…" : "re-audit"}
          </button>
        </div>
      </div>
      {err ? <div className="mono text-[11px] text-[#FF3B30] mt-3" data-testid="audit-error">{err}</div> : null}
      {!data?.ready && !err ? (
        <div className="mono text-[11px] text-zinc-500 py-6 text-center" data-testid="audit-empty">
          Build a portfolio first — then this grades it rule by rule before you upload to DraftKings.
        </div>
      ) : null}
      <div className="mt-3 space-y-3">
        {(data?.groups || []).map((g, gi) => {
          const tone = TONE[g.status] || TONE.warn;
          return (
            <div key={gi} className="border border-zinc-900" data-testid={`audit-group-${gi}`}>
              <div className="flex items-center justify-between gap-2 px-3 py-2 bg-black">
                <div className="mono text-[11px] text-white">{g.label}</div>
                <div className="flex items-center gap-2">
                  <span className="mono text-[9px] text-zinc-500">{g.bodies_used} bodies used</span>
                  <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-[2px] border ${tone.cls}`}>
                    {g.lineups}/{g.requested} · {tone.label}
                  </span>
                </div>
              </div>
              <table className="w-full mono text-[11px]">
                <tbody>
                  {(g.rules || []).map((r, ri) => {
                    const t = TONE[r.status] || TONE.warn;
                    const Icon = t.Icon;
                    return (
                      <tr key={ri} className="border-t border-zinc-900 align-top"
                          data-testid={`audit-rule-${r.rule.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`}>
                        <td className="px-3 py-2 w-6"><Icon size={13} className={t.cls.split(" ")[0]} /></td>
                        <td className="px-1 py-2 text-white whitespace-nowrap">{r.rule}</td>
                        <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">{r.expected}</td>
                        <td className={`px-3 py-2 whitespace-nowrap ${t.cls.split(" ")[0]}`}>{r.actual}</td>
                        <td className="px-3 py-2 text-zinc-500">{r.detail}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(g.top_exposure || []).length ? (
                <div className="px-3 py-2 border-t border-zinc-900 flex flex-wrap gap-1.5"
                     data-testid={`audit-exposure-${gi}`}>
                  {g.top_exposure.map((p) => (
                    <span key={p.dk_id} className="mono text-[9px] px-1.5 py-[2px] border border-zinc-800 text-zinc-400">
                      {p.name} <span className="text-white">{p.pct}%</span>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
